import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/portfolio.scss';

import Figma from '../assets/svg/figma.svg';
import Reactsvg from '../assets/svg/react.svg';
import Scss from '../assets/svg/scss.svg';
import Nextjs from '../assets/svg/nextjs.svg';
import Typescript from '../assets/svg/typescript.svg';
import SlackIcon from '../assets/svg/slack.svg';

import Star from '../assets/images/star.png';
import Ontime from '../assets/svg/ontime.svg';

// Project images - Update paths when images are added
import Freshair from '../assets/images/freshair.png';
import Light from '../assets/images/Light.png';
import Lightmain from '../assets/images/Lightmain.png';
import P2o from '../assets/images/p2o.png';
import P3o from '../assets/images/p3o.png';
import P4o from '../assets/images/p4o.png';
import P5o from '../assets/images/p5o.png';
import A2b from '../assets/images/a2b.png';
import Esign from '../assets/images/esign.png';
import Esign1 from '../assets/images/esign1.jpg';
import Esign2 from '../assets/images/esign2.jpg';
import Esignmain from '../assets/images/Esignmain.png';
import Elza from '../assets/images/elza.png';

const PortfolioIntro = () => {
  const { t } = useTranslation();
  const cardsRef = useRef([]);

  const projects = [
    {
      id: 1,
      title: t('portfolio.projects.ventilation'),
      companyType: t('portfolio.projects.ventilation_type'),
      description: t('portfolio.projects.ventilation_desc'),
      tech: "React, SCSS, Next.js",
      url: "https://www.freshaircare.nl/",
      image: Freshair,
      type: "website",
      year: '2025',
    },
    {
      id: 2,
      title: t('portfolio.projects.aiPlanner'),
      companyType: t('portfolio.projects.aiPlanner_type'),
      description: t('portfolio.projects.aiPlanner_desc'),
      tech: "Figma",
      url: Lightmain, // Open full image in new tab
      image: Light,
      images: [Light, P2o, P3o, P4o, P5o],
      fullDesign: Lightmain,
      type: "design",
      year: '2025',
    },
    {
      id: 3,
      title: t('portfolio.projects.fulfillment'),
      companyType: t('portfolio.projects.fulfillment_type'),
      description: t('portfolio.projects.fulfillment_desc'),
      tech: "React, SCSS, Next.js",
      url: "https://a2b-fulfilment.nl/",
      image: A2b,
      type: "website",
      year: '2025',
    },
    {
      id: 4,
      title: t('portfolio.projects.aiSignatures'),
      companyType: t('portfolio.projects.aiSignatures_type'),
      description: t('portfolio.projects.aiSignatures_desc'),
      tech: "Figma",
      url: Esignmain, // Open full image in new tab
      image: Esign,
      images: [Esign1, Esign2],
      fullDesign: Esignmain,
      type: "design",
      year: '2025',
    },
    {
      id: 5,
      title: t('portfolio.projects.glass'),
      companyType: t('portfolio.projects.glass_type'),
      description: t('portfolio.projects.glass_desc'),
      tech: "React, SCSS, Next.js",
      url: "https://elzaglas.nl/",
      image: Elza,
      type: "website",
      year: '2025',
    },
  ];

  useEffect(() => {
    const handleMouseMove = (e, card) => {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      card.style.setProperty('--mouse-x', `${x}%`);
      card.style.setProperty('--mouse-y', `${y}%`);
    };

    cardsRef.current.forEach((card) => {
      if (card) {
        const mouseMoveHandler = (e) => handleMouseMove(e, card);
        card.addEventListener('mousemove', mouseMoveHandler);
        
        // Cleanup
        card._mouseMoveHandler = mouseMoveHandler;
      }
    });

    return () => {
      const cards = cardsRef.current;
      cards.forEach((card) => {
        if (card && card._mouseMoveHandler) {
          card.removeEventListener('mousemove', card._mouseMoveHandler);
        }
      });
    };
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className="portfolio-hero">
        <div className="portfolio-hero__grid section-shell">
          {/* Left Column - Text Content */}
          <div className="portfolio-hero__text">
            <div className="portfolio-hero__label-row">
              <span className="portfolio-hero__eyebrow">{t('portfolio.label')}</span>
              <div className="portfolio-hero__counter">
                <span className="portfolio-hero__counter-number">80</span>
                <span className="portfolio-hero__counter-plus">+</span>
                <span className="portfolio-hero__counter-label">{t('portfolio.counter')}</span>
              </div>
            </div>
            <h1>{t('portfolio.title')}</h1>
            <p className="portfolio-hero__description">
              {t('portfolio.description')}
            </p>
            <div className="portfolio-hero__badges">
              <div className="portfolio-hero__badge">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1L10.163 5.39037L15 6.12124L11.5 9.52786L12.326 14.3787L8 12.0904L3.674 14.3787L4.5 9.52786L1 6.12124L5.837 5.39037L8 1Z" fill="rgb(255 255 0 / 80%)"/>
                </svg>
                <span>{t('portfolio.badge1')}</span>
              </div>
              <div className="portfolio-hero__badge">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="rgb(0 255 0 / 80%)" strokeWidth="1.5"/>
                  <path d="M5 8L7 10L11 6" stroke="rgb(0 255 0 / 80%)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{t('portfolio.badge2')}</span>
              </div>
              <div className="portfolio-hero__badge">
                <img src={Ontime} alt="Ontime" className="portfolio-hero__badge-icon" />
                <span>{t('portfolio.badge3')}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Visual Grid */}
          <div className="portfolio-hero__visual">
            <div className="portfolio-hero__visual-grid">
              {/* Figma Card */}
              <div className="portfolio-hero__visual-card portfolio-hero__visual-card--1" ref={(el) => (cardsRef.current[0] = el)}>
                <div className="portfolio-hero__visual-card-pattern">
                  {[...Array(100)].map((_, i) => (
                    <img key={i} src={Figma} alt="" className="portfolio-hero__visual-card-pattern-icon" />
                  ))}
                </div>
                <div className="portfolio-hero__visual-card-content">
                  <img src={Figma} alt="Figma" className="portfolio-hero__visual-card-icon" />
                  <span className="portfolio-hero__visual-card-label">Figma</span>
                </div>
              </div>

              {/* React Card */}
              <div className="portfolio-hero__visual-card portfolio-hero__visual-card--2" ref={(el) => (cardsRef.current[1] = el)}>
                <div className="portfolio-hero__visual-card-pattern">
                  {[...Array(100)].map((_, i) => (
                    <img key={i} src={Reactsvg} alt="" className="portfolio-hero__visual-card-pattern-icon" />
                  ))}
                </div>
                <div className="portfolio-hero__visual-card-content">
                  <img src={Reactsvg} alt="React" className="portfolio-hero__visual-card-icon" />
                  <span className="portfolio-hero__visual-card-label">React</span>
                </div>
              </div>

              {/* Sass Card */}
              <div className="portfolio-hero__visual-card portfolio-hero__visual-card--3" ref={(el) => (cardsRef.current[2] = el)}>
                <div className="portfolio-hero__visual-card-pattern">
                  {[...Array(100)].map((_, i) => (
                    <img key={i} src={Scss} alt="" className="portfolio-hero__visual-card-pattern-icon" />
                  ))}
                </div>
                <div className="portfolio-hero__visual-card-content">
                  <img src={Scss} alt="Sass" className="portfolio-hero__visual-card-icon" />
                  <span className="portfolio-hero__visual-card-label">Sass</span>
                </div>
              </div>

              {/* Next.js Card */}
              <div className="portfolio-hero__visual-card portfolio-hero__visual-card--4" ref={(el) => (cardsRef.current[3] = el)}>
                <div className="portfolio-hero__visual-card-pattern">
                  {[...Array(100)].map((_, i) => (
                    <img key={i} src={Nextjs} alt="" className="portfolio-hero__visual-card-pattern-icon" />
                  ))}
                </div>
                <div className="portfolio-hero__visual-card-content">
                  <img src={Nextjs} alt="Next.js" className="portfolio-hero__visual-card-icon" />
                  <span className="portfolio-hero__visual-card-label">Next.js</span>
                </div>
              </div>

              {/* TypeScript Card */}
              <div className="portfolio-hero__visual-card portfolio-hero__visual-card--5" ref={(el) => (cardsRef.current[4] = el)}>
                <div className="portfolio-hero__visual-card-pattern">
                  {[...Array(100)].map((_, i) => (
                    <img key={i} src={Typescript} alt="" className="portfolio-hero__visual-card-pattern-icon" />
                  ))}
                </div>
                <div className="portfolio-hero__visual-card-content">
                  <img src={Typescript} alt="TypeScript" className="portfolio-hero__visual-card-icon" />
                  <span className="portfolio-hero__visual-card-label">TypeScript</span>
                </div>
              </div>

              {/* Tailwind Card */}
              <div className="portfolio-hero__visual-card portfolio-hero__visual-card--6" ref={(el) => (cardsRef.current[5] = el)}>
                <div className="portfolio-hero__visual-card-pattern">
                  {[...Array(100)].map((_, i) => (
                    <img key={i} src={SlackIcon} alt="" className="portfolio-hero__visual-card-pattern-icon" />
                  ))}
                </div>
                <div className="portfolio-hero__visual-card-content">
                  <img src={SlackIcon} alt="Slack" className="portfolio-hero__visual-card-icon" />
                  <span className="portfolio-hero__visual-card-label">Slack</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="portfolio-projects">
        <div className="section-shell">
          <div className="portfolio-projects__grid">
            {projects.map((project) => (
              <article className="project-card" key={project.id}>
                <a href={project.url} className="project-card__link" target={project.url !== "#" ? "_blank" : undefined} rel={project.url !== "#" ? "noopener noreferrer" : undefined}>
                  <div className="project-card__image" style={{ backgroundImage: project.image ? `url(${project.image})` : undefined }}>
                    <div className="project-card__overlay">
                      <span className="project-card__year">{project.year}</span>
                    </div>
                  </div>
                  <div className="project-card__content">
                    <span className="project-card__category">{project.companyType}</span>
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                    <div className="project-card__stack">{project.tech}</div>
                  </div>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="portfolio-cta">
        <div className="section-shell">
          <div className="portfolio-cta__content">
            <h2>{t('portfolio.cta_title')}</h2>
            <p>
              {t('portfolio.cta_desc')}
            </p>
            <div className="portfolio-cta__buttons">
              <a href="/contact" className="portfolio-cta__button portfolio-cta__button--primary">
              <span className="btn__icon">
                <img src={Star} alt="" />
              </span>
                {t('portfolio.cta_primary')}
              </a>
              <a href="/services" className="portfolio-cta__button portfolio-cta__button--ghost">
                {t('portfolio.cta_secondary')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PortfolioIntro;
